/**
 * Demo Domain — Load Demo State
 *
 * Public entry point that produces the full demo MultiAccountStateV4
 * with all 8 learners, 3 parents, 3 teachers, 1 admin, and 3 classrooms.
 *
 * Boot Flow Refactor (mei 2026):
 * - Demo loading is the ONE path that is allowed to require all content,
 *   so this function ensures content is loaded before generating profiles.
 * - registerAllDemoProfiles() is called explicitly here (no side-effect
 *   imports) keeping the rest of the app tree-shakeable.
 */

import { ensureAllContentLoaded } from '../content';
import { generateDemoAccounts, seedDemoAssessments } from './seeding/accounts';
import { registerAllDemoProfiles, getDemoProfiles } from './registry';
import type { DemoState } from './types';
import { debug } from '../utils/logger';

/**
 * Build the canonical demo state. Awaits content first so that
 * `getModuleQuestions()` returns real questions inside the seeder.
 */
export async function loadDemoState(): Promise<DemoState> {
  // 1. Ensure the demo registry is populated.
  registerAllDemoProfiles();

  // 2. Demo MUST have all modules available.
  await ensureAllContentLoaded();

  // 3. Generate the full state via the existing seeder.
  const multiAccountState = generateDemoAccounts();

  // 4. Build a name → id map for downstream assessment seeding.
  const learnerIdsByName: Record<string, string> = {};
  for (const account of Object.values(multiAccountState.accounts.accountsById)) {
    if (account.role === 'learner') {
      learnerIdsByName[account.displayName] = account.id;
    }
  }
  debug(`🎭 Demo state loaded — ${getDemoProfiles().length} profiles registered, ` + `${Object.keys(learnerIdsByName).length} learners generated`);
  return {
    multiAccountState,
    learnerIdsByName
  };
}

/**
 * Seed the demo assessment data for a freshly-loaded demo state.
 * Awaits — so callers can safely render the assessment screens immediately.
 */
export async function seedDemoAssessmentsForState(state: DemoState, options?: {
  teacherId?: string;
  classroomId?: string;
}): Promise<void> {
  const {
    learnerIdsByName
  } = state;
  const required = ['Djayden', 'Sophie', 'Emma', 'Noah', 'Liam', 'Noor'] as const;
  const ids: Record<string, string> = {};
  for (const name of required) {
    ids[name.toLowerCase()] = learnerIdsByName[name] ?? '';
  }
  if (!ids.djayden || !ids.sophie) {
    debug('Demo assessments skipped — required learners missing.');
    return;
  }
  await seedDemoAssessments(ids, options?.teacherId, options?.classroomId);
}